import React, { createContext, useContext, useRef } from 'react'

import { identity } from 'es-toolkit'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import { getActiveConfirmationStatusFilters } from '@/components/library-v2/lib'
import type {
  PeopleStore,
  PeopleStoreState,
} from '@/components/library-v2/people/Table/store'
import { createPeopleStore } from '@/components/library-v2/people/Table/store'

import type { RelationshipStatus } from '@/types/library/Entity'

interface PeopleContextProviderProps {
  children: React.ReactNode
}

export const PeopleContext = createContext<PeopleStore | undefined>(undefined)

export const PeopleContextProvider: React.FC<PeopleContextProviderProps> = ({
  children,
}) => {
  const storeRef = useRef<PeopleStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createPeopleStore()
  }

  return (
    <PeopleContext.Provider value={storeRef.current}>
      {children}
    </PeopleContext.Provider>
  )
}

export const usePeopleContext = () => {
  const context = useContext(PeopleContext)
  if (!context) {
    throw new Error(
      'usePeopleContext must be used within a PeopleContextProvider',
    )
  }
  return context
}

export const usePeopleStore = <T,>(
  selector: (state: PeopleStoreState) => T = identity as (
    state: PeopleStoreState,
  ) => T,
) => {
  const context = usePeopleContext()
  if (!context) {
    throw new Error(
      'usePeopleStore must be used within a PeopleContextProvider',
    )
  }
  return useStore(context, selector)
}

export const usePeopleFilters = () => {
  return usePeopleStore(useShallow((state) => state.filters))
}

export const usePeopleSetFilters = () => {
  return usePeopleStore((state) => state.setFilters)
}

export const usePeopleAddRelationshipStatusFilter = () => {
  return usePeopleStore((state) => state.addRelationshipStatusFilter)
}

export const usePeopleResetFilters = () => {
  return usePeopleStore((state) => state.resetFilters)
}

export const usePeopleRoleFilters = () => {
  return usePeopleStore(useShallow((state) => state.filters.roles))
}

export const usePeopleDepartmentIdFilters = () => {
  return usePeopleStore(useShallow((state) => state.filters.department_ids))
}

export const usePeopleCompanyIdFilters = () => {
  return usePeopleStore(useShallow((state) => state.filters.company_ids))
}

export const usePeopleAgencyIdFilters = () => {
  return usePeopleStore(useShallow((state) => state.filters.agency_ids))
}

export const usePeopleConfirmationStatusFilters = () => {
  return usePeopleStore(
    useShallow((state) => state.filters.confirmationStatuses),
  )
}

export const usePeopleActiveConfirmationStatusFilters = () => {
  return usePeopleStore(
    useShallow((state) =>
      getActiveConfirmationStatusFilters(state.filters.confirmationStatuses),
    ),
  )
}

export const usePeopleInternalStatusFilters = () => {
  return usePeopleStore(useShallow((state) => state.filters.internalStatuses))
}

export const usePeopleRelationshipStatusFilters = () => {
  return usePeopleStore(
    useShallow((state) => state.filters.relationshipStatuses),
  )
}

export const usePeopleRelationshipStatusFilterIsActive = (
  status: RelationshipStatus | null,
) => {
  return usePeopleStore(
    (state) =>
      status !== null && state.filters.relationshipStatuses.includes(status),
  )
}

export const usePeopleSearchQuery = () => {
  return usePeopleStore(useShallow((state) => state.searchQuery))
}

export const usePeopleSetSearchQuery = () => {
  return usePeopleStore((state) => state.setSearchQuery)
}

export const usePeopleSelectedPersonIds = () => {
  return usePeopleStore(useShallow((state) => state.selectedPersonIds))
}

export const usePeopleSetSelectedPersonIds = () => {
  return usePeopleStore((state) => state.setSelectedPersonIds)
}

export const usePeopleLastSelectedRowId = () => {
  return usePeopleStore((state) => state.lastSelectedRowId)
}

export const usePeopleSetLastSelectedRowId = () => {
  return usePeopleStore((state) => state.setLastSelectedRowId)
}

export const usePeoplePagination = () => {
  return usePeopleStore(useShallow((state) => state.pagination))
}

export const usePeopleSetPagination = () => {
  return usePeopleStore((state) => state.setPagination)
}

export const usePeopleDeleteDialogOpen = () => {
  return usePeopleStore((state) => state.deleteDialogOpen)
}

export const usePeopleSetDeleteDialogOpen = () => {
  return usePeopleStore((state) => state.setDeleteDialogOpen)
}

export const usePeopleMergeDialogOpen = () => {
  return usePeopleStore((state) => state.mergeDialogOpen)
}

export const usePeopleSetMergeDialogOpen = () => {
  return usePeopleStore((state) => state.setMergeDialogOpen)
}

export const usePeopleConfirmDialogOpen = () => {
  return usePeopleStore((state) => state.confirmDialogOpen)
}

export const usePeopleSetConfirmDialogOpen = () => {
  return usePeopleStore((state) => state.setConfirmDialogOpen)
}

export const usePeopleCreateDialogOpen = () => {
  return usePeopleStore((state) => state.createDialogOpen)
}

export const usePeopleSetCreateDialogOpen = () => {
  return usePeopleStore((state) => state.setCreateDialogOpen)
}

export const usePeopleImportDialogOpen = () => {
  return usePeopleStore((state) => state.importDialogOpen)
}

export const usePeopleSetImportDialogOpen = () => {
  return usePeopleStore((state) => state.setImportDialogOpen)
}
